/**
 * Central TanStack Query key factory.
 *
 * Every `useQuery` in the app reaches for a key from here rather than
 * writing ad-hoc tuples; invalidation becomes predictable and IDE
 * "find references" shows every callsite.
 *
 * Convention:
 *   qk.<domain>.<operation>(...args)
 * Keys are arrays starting with the domain so partial invalidation
 * (e.g. `queryClient.invalidateQueries({ queryKey: qk.workers.all })`)
 * Just Works.
 */

export const qk = {
  me: {
    all: ['me'] as const,
    current: () => [...qk.me.all, 'current'] as const,
  },
  featureFlags: {
    all: ['feature-flags'] as const,
    /** The app-wide provider's cache: a `{ key: value }` FlagMap. */
    current: () => [...qk.featureFlags.all, 'current'] as const,
    /**
     * The settings screen's cache: the raw `{ flags: [...] }` rows.
     *
     * Deliberately a different key from `current()`. Both fetch the
     * same endpoint but keep different shapes, and sharing one key
     * meant whichever mounted first won — always the provider, since
     * it wraps the whole app. The settings screen then read `.flags`
     * off a FlagMap, got `undefined`, and rendered "No feature flags
     * configured" over a tenant that had them.
     */
    admin: () => [...qk.featureFlags.all, 'admin'] as const,
  },
  workers: {
    all: ['workers'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...qk.workers.all, 'list', filters ?? {}] as const,
    byId: (id: string) => [...qk.workers.all, 'byId', id] as const,
    subresource: (id: string, kind: string) =>
      [...qk.workers.byId(id), kind] as const,
  },
  companies: {
    all: ['companies'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...qk.companies.all, 'list', filters ?? {}] as const,
    byId: (id: string) => [...qk.companies.all, 'byId', id] as const,
    contacts: (id: string) => [...qk.companies.byId(id), 'contacts'] as const,
    workPlaces: (id: string) =>
      [...qk.companies.byId(id), 'work-places'] as const,
    /** Generic sub-resource key — used by the shared SubResourceManager. */
    subresource: (id: string, kind: string) =>
      [...qk.companies.byId(id), kind] as const,
  },
  subcompanies: {
    all: ['subcompanies'] as const,
    list: () => [...qk.subcompanies.all, 'list'] as const,
  },
  licenseTypes: {
    all: ['license-types'] as const,
    list: () => [...qk.licenseTypes.all, 'list'] as const,
  },
  tools: {
    all: ['tools'] as const,
    list: () => [...qk.tools.all, 'list'] as const,
  },
  workerLicenseCategories: {
    all: ['worker-license-categories'] as const,
    list: (licenseId: string) =>
      [...qk.workerLicenseCategories.all, 'list', licenseId] as const,
  },
  contracts: {
    all: ['contracts'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...qk.contracts.all, 'list', filters ?? {}] as const,
    byId: (id: string) => [...qk.contracts.all, 'byId', id] as const,
    templates: {
      all: ['contract-templates'] as const,
      list: () => ['contract-templates', 'list'] as const,
    },
  },
  leave: {
    all: ['leave'] as const,
    config: (workerId?: string) =>
      [...qk.leave.all, 'config', workerId ?? 'agency'] as const,
    balance: (workerId: string) =>
      [...qk.leave.all, 'balance', workerId] as const,
    roster: () => [...qk.leave.all, 'roster'] as const,
    events: (workerId: string) =>
      [...qk.leave.all, 'events', workerId] as const,
    requests: (filters?: Record<string, unknown>) =>
      [...qk.leave.all, 'requests', filters ?? {}] as const,
  },
  dashboard: {
    stats: (filters?: Record<string, unknown>) =>
      ['dashboard', 'stats', filters ?? {}] as const,
  },
  vacancies: {
    all: ['vacancies'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...qk.vacancies.all, 'list', filters ?? {}] as const,
    byId: (id: string) => [...qk.vacancies.all, 'byId', id] as const,
  },
  translations: {
    all: ['translations'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...qk.translations.all, 'list', filters ?? {}] as const,
  },
  documents: {
    templates: {
      all: ['document-templates'] as const,
      list: (kind?: string) =>
        ['document-templates', 'list', kind ?? 'all'] as const,
    },
  },
  rendered: {
    download: (id: string) => ['rendered-documents', id, 'download'] as const,
  },
  postalCodes: {
    lookup: (code: string) => ['postal-codes', code] as const,
  },
  audit: {
    list: (filters?: Record<string, unknown>) =>
      ['audit', 'list', filters ?? {}] as const,
  },
  wageFactors: {
    all: ['wage-factors'] as const,
    current: () => [...qk.wageFactors.all, 'current'] as const,
  },
} as const;
